/* Xiaomi 2026 · Templates Admin (Sprint 2A frontend)
 *
 * /xiaomi/admin/templates · CEO + xiaomi_admin
 *
 * Surfaces the multilingual template matrix:
 *   • 10 Wave-1 templates × 13 languages = 130 cells
 *   • Each cell renders a status pill (or blank if not yet translated)
 *   • Click a cell → drawer with English source + translated body side-by-side
 *   • Inline edit · Approve · Translate-to-all · Submit actions
 *
 * Visual language matches Ayush's paper/orange aesthetic via .xiaomi-theme
 * (palette inherited from XiaomiShell wrapper).
 */
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LANG_LABELS = {
  en: 'English',
  hi: 'Hindi · हिन्दी',
  mr: 'Marathi · मराठी',
  bn: 'Bengali · বাংলা',
  gu: 'Gujarati · ગુજરાતી',
  kn: 'Kannada · ಕನ್ನಡ',
  ta: 'Tamil · தமிழ்',
  ml: 'Malayalam · മലയാളം',
  te: 'Telugu · తెలుగు',
  pa: 'Punjabi · ਪੰਜਾਬੀ',
  or: 'Odia · ଓଡ଼ିଆ',
  as: 'Assamese · অসমীয়া',
  ne: 'Nepali · नेपाली',
};

const STATUS_META = {
  draft:                { label: 'Draft',     bg: '#f0ece3', fg: '#6b6155', icon: '○' },
  ai_drafted_pending:   { label: 'AI · pending review', bg: '#fff3e0', fg: '#d98a1f', icon: '🤖' },
  native_reviewed:      { label: 'Reviewed',  bg: '#eaf1f7', fg: '#2b5d8a', icon: '👁' },
  approved:             { label: 'Approved',  bg: '#dcf8e8', fg: '#128c7e', icon: '✓' },
  submitted:            { label: 'Submitted', bg: '#fff3e6', fg: '#e85c00', icon: '↑' },
  meta_approved:        { label: 'Live',      bg: '#15110d', fg: '#f3e6cf', icon: '●' },
  meta_rejected:        { label: 'Rejected',  bg: '#fbe9e3', fg: '#c0492f', icon: '✕' },
};

const auth = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
});

export default function XiaomiAdminTemplates() {
  const [data, setData] = useState({
    templates: [],
    supported_languages: [],
    wave1_template_keys: [],
  });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [activeCell, setActiveCell] = useState(null); // {template_key, language}
  const [filterStatus, setFilterStatus] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/xiaomi/templates`, auth());
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load templates');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Build [template_key][language] lookup
  const matrix = useMemo(() => {
    const m = {};
    for (const t of data.templates) {
      if (!m[t.template_key]) m[t.template_key] = {};
      m[t.template_key][t.language] = t;
    }
    return m;
  }, [data.templates]);

  const templateKeys = data.wave1_template_keys.length
    ? data.wave1_template_keys
    : Object.keys(matrix).sort();

  const langs = data.supported_languages.length
    ? data.supported_languages
    : ['en', 'hi', 'mr', 'bn', 'gu', 'kn', 'ta', 'ml', 'te', 'pa', 'or', 'as', 'ne'];

  // ── header counts ────────────────────────────────────────────────
  const counts = useMemo(() => {
    const out = { total: 0, by_status: {} };
    for (const t of data.templates) {
      out.total += 1;
      out.by_status[t.status] = (out.by_status[t.status] || 0) + 1;
    }
    return out;
  }, [data.templates]);

  // ── actions ──────────────────────────────────────────────────────
  const seedEnglish = async () => {
    setBusy(true);
    try {
      const r = await axios.post(
        `${API}/xiaomi/templates/admin/seed-english`, {}, auth(),
      );
      toast.success(
        `Seeded · ${r.data.wrote_new} new · ${r.data.updated_existing} updated`,
      );
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Seed failed');
    } finally {
      setBusy(false);
    }
  };

  const exportPdf = async () => {
    setBusy(true);
    try {
      const r = await axios.get(
        `${API}/xiaomi/templates/admin/preview-pdf`,
        { ...auth(), responseType: 'blob', timeout: 60_000 },
      );
      const blob = new Blob([r.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Xiaomi_Wave1_Translations_${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded · ready for Nisha\'s network');
    } catch (e) {
      toast.error('PDF export failed');
    } finally {
      setBusy(false);
    }
  };

  const translateAll = async (template_key) => {
    if (!window.confirm(
      `Translate "${template_key}" into all 12 non-English languages?\n\n` +
      `~60-120 seconds · ~$0.15 in LLM spend · existing translations stay unless force.`,
    )) return;
    setBusy(true);
    try {
      const r = await axios.post(
        `${API}/xiaomi/templates/${template_key}/translate`,
        { target_langs: 'all', force: false },
        { ...auth(), timeout: 300_000 },
      );
      toast.success(`Done · ${r.data.total_done} languages translated`);
      if ((r.data.errors || []).length) {
        toast.error(`${r.data.errors.length} language(s) failed`);
      }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Translation failed');
    } finally {
      setBusy(false);
    }
  };

  // ── render ───────────────────────────────────────────────────────
  if (loading) {
    return <div className="text-soft text-sm" data-testid="xiaomi-templates-loading">Loading templates…</div>;
  }

  return (
    <div data-testid="xiaomi-admin-templates">
      <div className="flex items-end justify-between mb-2">
        <div>
          <h1 style={{ fontFamily: 'Fraunces, serif' }}>Templates</h1>
          <p className="text-soft text-sm">
            Wave 1 · 10 templates · 13 languages · 130 cells
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            className="btn-ghost"
            onClick={exportPdf}
            disabled={busy}
            data-testid="xiaomi-templates-export-pdf"
            title="Generate side-by-side review PDF for Nisha's network"
          >
            ⇩ Export PDF
          </button>
          <button
            className="btn-ghost"
            onClick={seedEnglish}
            disabled={busy}
            data-testid="xiaomi-templates-seed-english"
          >
            Seed English
          </button>
        </div>
      </div>

      {/* count strip */}
      <div className="flex flex-wrap gap-2 mb-6">
        <CountChip label="Total cells" value={counts.total} />
        {Object.entries(STATUS_META).map(([k, m]) => (
          <CountChip
            key={k}
            label={m.label}
            value={counts.by_status[k] || 0}
            bg={m.bg}
            fg={m.fg}
            icon={m.icon}
            active={filterStatus === k}
            onClick={() => setFilterStatus(filterStatus === k ? '' : k)}
            testid={`xiaomi-templates-count-${k}`}
          />
        ))}
      </div>

      {/* matrix */}
      <div className="x-card overflow-auto" data-testid="xiaomi-templates-matrix">
        <table className="w-full text-xs">
          <thead>
            <tr>
              <th className="sticky left-0 bg-panel border-b border-line p-3 text-left text-[11px] uppercase tracking-wider text-soft min-w-[260px]">
                Template
              </th>
              {langs.map((l) => (
                <th
                  key={l}
                  className="border-b border-line p-2 text-center text-[10px] uppercase tracking-wider text-soft min-w-[68px]"
                  title={LANG_LABELS[l] || l}
                >
                  {l}
                </th>
              ))}
              <th className="border-b border-line p-2 text-[10px] uppercase tracking-wider text-soft min-w-[110px]">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {templateKeys.map((tkey) => {
              const enRow = matrix[tkey]?.en;
              return (
                <tr key={tkey} className="border-b border-line-soft last:border-0">
                  <td className="sticky left-0 bg-panel p-3 border-r border-line-soft">
                    <div className="font-semibold text-ink truncate max-w-[300px]">
                      {tkey.replace(/^xiaomi_/, '').replace(/_/g, ' ')}
                    </div>
                    <div className="text-[10px] text-soft mt-0.5">
                      {enRow?.lifecycle_key_hint || '—'}
                      {enRow?.variant && (
                        <span className="ml-2">· {enRow.variant}</span>
                      )}
                    </div>
                  </td>
                  {langs.map((l) => {
                    const cell = matrix[tkey]?.[l];
                    const dimmed = filterStatus && cell?.status !== filterStatus;
                    return (
                      <td
                        key={l}
                        className="p-1 text-center"
                        data-testid={`xiaomi-templates-cell-${tkey}-${l}`}
                      >
                        {cell ? (
                          <button
                            type="button"
                            className="w-full"
                            onClick={() => setActiveCell({ template_key: tkey, language: l })}
                            style={{ opacity: dimmed ? 0.25 : 1, transition: 'opacity .15s' }}
                          >
                            <StatusPill status={cell.status} compact />
                          </button>
                        ) : (
                          <span className="text-soft text-[11px]">—</span>
                        )}
                      </td>
                    );
                  })}
                  <td className="p-2">
                    <button
                      className="btn-mi"
                      onClick={() => translateAll(tkey)}
                      disabled={busy || !enRow}
                      data-testid={`xiaomi-templates-translate-all-${tkey}`}
                      title={enRow ? 'Run Claude on 12 non-English langs' : 'Seed English first'}
                    >
                      Translate all
                    </button>
                  </td>
                </tr>
              );
            })}
            {templateKeys.length === 0 && (
              <tr>
                <td
                  colSpan={langs.length + 2}
                  className="p-10 text-center text-soft"
                >
                  No templates yet. Click <b>Seed English</b> above to load the 10 Wave-1 seeds.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {activeCell && (
        <TemplateDetailDrawer
          templateKey={activeCell.template_key}
          language={activeCell.language}
          onClose={() => setActiveCell(null)}
          onAfterChange={load}
        />
      )}
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────
// Sub-components
// ─────────────────────────────────────────────────────────────────────

function CountChip({ label, value, bg, fg, icon, active, onClick, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="chip"
      style={{
        background: bg || '#fff',
        color: fg || '#15110d',
        borderColor: active ? '#15110d' : 'var(--line)',
        borderWidth: active ? 2 : 1,
        cursor: onClick ? 'pointer' : 'default',
      }}
      data-testid={testid}
    >
      {icon && <span style={{ marginRight: 4 }}>{icon}</span>}
      {label} · <strong style={{ marginLeft: 4 }}>{value}</strong>
    </button>
  );
}

function StatusPill({ status, compact }) {
  const m = STATUS_META[status] || STATUS_META.draft;
  return (
    <span
      className="chip"
      style={{
        background: m.bg,
        color: m.fg,
        borderColor: m.bg,
        fontSize: compact ? 9.5 : 10.5,
        padding: compact ? '2px 6px' : '4px 9px',
      }}
      data-testid={`xiaomi-template-status-${status}`}
    >
      <span style={{ marginRight: 3 }}>{m.icon}</span>
      {compact ? m.label.split(' ')[0] : m.label}
    </span>
  );
}


function TemplateDetailDrawer({ templateKey, language, onClose, onAfterChange }) {
  const [tpl, setTpl] = useState(null);
  const [enTpl, setEnTpl] = useState(null);
  const [editBody, setEditBody] = useState('');
  const [busy, setBusy] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [single, enSingle] = await Promise.all([
          axios.get(`${API}/xiaomi/templates/${templateKey}/${language}`, auth()),
          language === 'en'
            ? Promise.resolve(null)
            : axios.get(`${API}/xiaomi/templates/${templateKey}/en`, auth())
              .catch(() => null),
        ]);
        if (!alive) return;
        setTpl(single.data.template);
        setEnTpl(enSingle?.data?.template || null);
        setEditBody(single.data.template.body || '');
        setDirty(false);
      } catch (e) {
        toast.error(e?.response?.data?.detail || 'Failed to load template');
        onClose();
      }
    })();
    return () => { alive = false; };
  }, [templateKey, language, onClose]);

  if (!tpl) {
    return (
      <DrawerOverlay onClose={onClose}>
        <div className="p-10 text-soft text-sm">Loading…</div>
      </DrawerOverlay>
    );
  }

  const translate = async () => {
    setBusy(true);
    try {
      const r = await axios.post(
        `${API}/xiaomi/templates/${templateKey}/translate`,
        { target_langs: [language], force: true },
        { ...auth(), timeout: 90_000 },
      );
      if ((r.data.errors || []).length) {
        toast.error(`Translation error: ${r.data.errors[0]?.error || 'unknown'}`);
      } else {
        toast.success('Re-translated via Claude');
        const fresh = await axios.get(
          `${API}/xiaomi/templates/${templateKey}/${language}`, auth(),
        );
        setTpl(fresh.data.template);
        setEditBody(fresh.data.template.body || '');
        setDirty(false);
        onAfterChange?.();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Translation failed');
    } finally {
      setBusy(false);
    }
  };

  const saveOverride = async () => {
    setBusy(true);
    try {
      const r = await axios.patch(
        `${API}/xiaomi/templates/${templateKey}/${language}`,
        { body: editBody, status: 'native_reviewed' },
        auth(),
      );
      setTpl(r.data.template);
      setDirty(false);
      toast.success('Saved · marked as native-reviewed');
      onAfterChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setBusy(false);
    }
  };

  const approve = async () => {
    setBusy(true);
    try {
      const r = await axios.post(
        `${API}/xiaomi/templates/${templateKey}/${language}/approve`, {}, auth(),
      );
      setTpl(r.data.template);
      toast.success('Approved');
      onAfterChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Approve failed');
    } finally {
      setBusy(false);
    }
  };

  const submit = async () => {
    setBusy(true);
    try {
      const r = await axios.post(
        `${API}/xiaomi/templates/${templateKey}/${language}/submit`, {}, auth(),
      );
      setTpl(r.data.template);
      toast.success('Submitted (stub · Sprint 2B wires Gupshup)');
      onAfterChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Submit failed');
    } finally {
      setBusy(false);
    }
  };

  const aiMeta = tpl.ai_translation_metadata;

  return (
    <DrawerOverlay onClose={onClose}>
      <div className="flex items-start justify-between p-6 border-b border-line">
        <div>
          <div className="text-[11px] uppercase tracking-wider text-soft">
            {LANG_LABELS[language] || language}
          </div>
          <h2 style={{ fontFamily: 'Fraunces, serif' }} className="mt-1">
            {templateKey.replace(/^xiaomi_/, '').replace(/_/g, ' ')}
          </h2>
          <div className="flex gap-2 mt-2 items-center">
            <StatusPill status={tpl.status} />
            {tpl.variant && (
              <span className="chip">variant · {tpl.variant}</span>
            )}
            {tpl.tone && (
              <span className="text-[11px] text-soft">{tpl.tone}</span>
            )}
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="btn-ghost"
          data-testid="xiaomi-template-drawer-close"
        >
          ✕ Close
        </button>
      </div>

      {/* Variables strip */}
      {(tpl.variables || []).length > 0 && (
        <div className="px-6 py-3 bg-panel2 border-b border-line-soft text-[11px] flex flex-wrap gap-3">
          <span className="text-soft uppercase tracking-wider">Variables:</span>
          {tpl.variables.map((v) => (
            <span key={v.index} className="chip" style={{ fontSize: 10 }}>
              <strong className="mr-1">{`{{${v.index}}}`}</strong>
              {v.name}
              <span className="text-soft ml-1.5">({v.example})</span>
            </span>
          ))}
        </div>
      )}

      <div className="flex-1 grid lg:grid-cols-2 gap-0 overflow-auto">
        {/* English source */}
        {language !== 'en' && enTpl && (
          <div className="p-6 border-r border-line-soft bg-panel2">
            <div className="text-[11px] uppercase tracking-wider text-soft mb-2">
              English source · {enTpl.status}
            </div>
            <pre className="whitespace-pre-wrap font-body text-sm leading-relaxed text-ink"
                 style={{ fontFamily: 'var(--fnt-body)' }}>
              {enTpl.body}
            </pre>
          </div>
        )}

        {/* Editable target body */}
        <div className={`p-6 ${language === 'en' ? 'lg:col-span-2' : ''}`}>
          <div className="flex items-center justify-between mb-2">
            <div className="text-[11px] uppercase tracking-wider text-soft">
              {language === 'en' ? 'English body (canonical)' : `${LANG_LABELS[language] || language} translation`}
            </div>
            {aiMeta && (
              <div className="text-[10px] text-soft">
                {aiMeta.model} · {new Date(aiMeta.translated_at).toLocaleDateString()}
              </div>
            )}
          </div>
          <textarea
            className="x-input"
            style={{
              minHeight: 280, fontSize: 13.5, fontFamily: 'var(--fnt-body)',
              lineHeight: 1.55, whiteSpace: 'pre-wrap',
            }}
            value={editBody}
            onChange={(e) => { setEditBody(e.target.value); setDirty(true); }}
            data-testid="xiaomi-template-body-editor"
            spellCheck={false}
          />
          {aiMeta && (aiMeta.flags || []).length > 0 && (
            <div className="mt-2 text-[11px] flex flex-wrap gap-2">
              <span className="text-soft">Flags:</span>
              {aiMeta.flags.map((f, i) => (
                <span key={i} className="chip" style={{ background: '#fbe9e3', color: '#c0492f', borderColor: '#fbe9e3' }}>
                  {f}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Footer actions */}
      <div className="flex items-center justify-between p-4 border-t border-line bg-panel2 gap-2 flex-wrap">
        <div className="text-[11px] text-soft">
          {tpl.reviewed_by && <>reviewed by {tpl.reviewed_by} · </>}
          {tpl.approved_by && <>approved by {tpl.approved_by} · </>}
          {tpl.submitted_by && <>submitted by {tpl.submitted_by}</>}
        </div>
        <div className="flex gap-2 flex-wrap">
          {language !== 'en' && (
            <button
              type="button"
              className="btn-ghost"
              onClick={translate}
              disabled={busy}
              data-testid="xiaomi-template-retranslate"
            >
              ↻ Re-translate via Claude
            </button>
          )}
          <button
            type="button"
            className="btn-ghost"
            onClick={saveOverride}
            disabled={busy || !dirty}
            data-testid="xiaomi-template-save-override"
          >
            Save edits → Reviewed
          </button>
          <button
            type="button"
            className="btn-mi"
            onClick={approve}
            disabled={busy || tpl.status === 'approved' || tpl.status === 'submitted' || tpl.status === 'meta_approved'}
            data-testid="xiaomi-template-approve"
          >
            ✓ Approve
          </button>
          <button
            type="button"
            className="btn-dark"
            onClick={submit}
            disabled={busy || tpl.status !== 'approved'}
            data-testid="xiaomi-template-submit"
            title={tpl.status !== 'approved' ? 'Approve first' : 'Submit to Gupshup (stub · Sprint 2B)'}
          >
            ↑ Submit
          </button>
        </div>
      </div>
    </DrawerOverlay>
  );
}


function DrawerOverlay({ children, onClose }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-stretch justify-end"
      style={{ background: 'rgba(20,15,8,0.45)' }}
      onClick={onClose}
      data-testid="xiaomi-template-drawer-overlay"
    >
      <div
        className="x-modal flex flex-col w-full max-w-[920px] h-full"
        onClick={(e) => e.stopPropagation()}
        style={{ borderRadius: 0 }}
      >
        {children}
      </div>
    </div>
  );
}
